import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { QRCodeSVG } from "qrcode.react";
import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BusinessCard } from "@/components/BusinessCard";
import { PosterCard } from "@/components/PosterCard";
import { Sparkles, Mail, Loader2, Package } from "lucide-react";
import { track } from "@/lib/track";
import { sendPrintEmailFn } from "@/lib/email/email-senders.functions";
import { useStripeCheckout } from "@/hooks/use-stripe-checkout";
import { useAuth } from "@/hooks/use-auth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { PrintTipsDialog, shouldShowPrintTips } from "@/components/PrintTipsDialog";
import { StarterKitAddressDialog } from "@/components/StarterKitAddressDialog";
import { LOCALE_GROUPS, LOCALE_LABELS, getLocaleDefaults } from "@/lib/onboarding-defaults";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/_authenticated/poster/$slug")({
  component: PosterPage,
  validateSearch: (search: Record<string, unknown>) => ({
    welcome: search.welcome === "1" || search.welcome === 1 || search.welcome === true || search.welcome === "true" ? true : false,
    starter_kit: typeof search.starter_kit === "string" ? search.starter_kit : undefined,
  }),
});

function PosterPage() {
  const { t } = useTranslation();
  const { slug } = Route.useParams();
  const { welcome, starter_kit } = Route.useSearch();
  const { user } = useAuth();
  const { loading: checkoutLoading, checkoutDialog } = useStripeCheckout();
  const [merchant, setMerchant] = useState<{
    id: string;
    business_name: string;
    scan_token: string;
    brand_primary_color?: string | null;
    poster_top_text?: string | null;
    poster_bottom_text?: string | null;
    card_line1?: string | null;
    card_line2?: string | null;
    onboarding_completed?: boolean;
    locale?: string | null;
  } | null>(null);
  const [showPulse, setShowPulse] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showOnboardingDialog, setShowOnboardingDialog] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [showPrintTips, setShowPrintTips] = useState(false);
  const [pendingDownload, setPendingDownload] = useState<(() => void) | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [topText, setTopText] = useState("for today");
  const [bottomText, setBottomText] = useState("Here is a gift for next time!");
  const [cardLine1, setCardLine1] = useState("Here is a gift for next time!");
  const [cardLine2, setCardLine2] = useState("for today");
  const [selectedLocale, setSelectedLocale] = useState<string>("en");
  const [resetScope, setResetScope] = useState<"poster" | "card" | null>(null);
  const posterRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const tenCardRef = useRef<HTMLDivElement | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    supabase
      .from("merchants")
      .select("id, business_name, scan_token, brand_primary_color, poster_top_text, poster_bottom_text, card_line1, card_line2, onboarding_completed, locale")
      .eq("slug", slug)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setMerchant(data);
          if (data.poster_top_text) setTopText(data.poster_top_text);
          if (data.poster_bottom_text) setBottomText(data.poster_bottom_text);
          if (data.card_line1) setCardLine1(data.card_line1);
          if (data.card_line2) setCardLine2(data.card_line2);
          if (data.locale) setSelectedLocale(data.locale);
          if (welcome) {
            setShowPulse(true);
            setShowWelcome(true);
            setShowOnboardingDialog(true);
            setTimeout(() => setShowPulse(false), 8000);
          }
        }
        setLoaded(true);
      });
  }, [slug, welcome]);

  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const starterKitToastFired = useRef(false);

  // After returning from Stripe checkout success we just show a toast — the order row
  // was already created (with the address) before checkout opened. Guard
  // against StrictMode double-invoke and re-renders by firing once per mount
  // and stripping the query param.
  const navigate = Route.useNavigate();
  useEffect(() => {
    if (starter_kit === "success" && !starterKitToastFired.current) {
      starterKitToastFired.current = true;
      toast.success(t("poster.starterKitSuccess"));
      navigate({ search: (prev: { welcome: boolean; starter_kit: string | undefined }) => ({ ...prev, starter_kit: undefined }), replace: true });
    }
  }, [starter_kit, navigate, t]);



  const handleOrderStarterKit = useCallback(() => {
    if (!merchant) return;
    track("starter_kit_checkout_opened", merchant.id);
    setAddressDialogOpen(true);
  }, [merchant]);


  const saveField = useCallback(
    (field: "poster_top_text" | "poster_bottom_text" | "card_line1" | "card_line2", value: string) => {
      if (!merchant) return;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        const update =
          field === "poster_top_text" ? { poster_top_text: value }
          : field === "poster_bottom_text" ? { poster_bottom_text: value }
          : field === "card_line1" ? { card_line1: value }
          : { card_line2: value };
        supabase
          .from("merchants")
          .update(update)
          .eq("id", merchant.id)
          .then(() => {});
      }, 800);
    },
    [merchant]
  );

  const applyLocaleDefaults = useCallback(
    async (locale: string, scope: "all" | "poster" | "card") => {
      if (!merchant) return;
      const d = getLocaleDefaults(locale);
      const update: {
        poster_top_text?: string;
        poster_bottom_text?: string;
        card_line1?: string;
        card_line2?: string;
      } = {};
      if (scope === "all" || scope === "poster") {
        setTopText(d.posterTop);
        setBottomText(d.posterBottom);
        update.poster_top_text = d.posterTop;
        update.poster_bottom_text = d.posterBottom;
      }
      if (scope === "all" || scope === "card") {
        setCardLine1(d.cardLine1);
        setCardLine2(d.cardLine2);
        update.card_line1 = d.cardLine1;
        update.card_line2 = d.cardLine2;
      }
      await supabase.from("merchants").update(update).eq("id", merchant.id);
    },
    [merchant]
  );

  const handleLocaleChange = useCallback(
    async (locale: string) => {
      if (!merchant) return;
      setSelectedLocale(locale);
      await supabase.from("merchants").update({ locale }).eq("id", merchant.id);
      await applyLocaleDefaults(locale, "all");
      toast.success(t("poster.switchedTo", { language: LOCALE_LABELS[locale] || locale }));
    },
    [merchant, applyLocaleDefaults, t]
  );

  const handleReset = useCallback(
    async (scope: "poster" | "card") => {
      await applyLocaleDefaults(selectedLocale, scope);
      toast.success(t("poster.resetToDefaults", { language: LOCALE_LABELS[selectedLocale] || selectedLocale }));
    },
    [selectedLocale, applyLocaleDefaults, t]
  );

  const handleEmailPrintFiles = useCallback(async () => {
    if (!merchant) return;
    setEmailSending(true);
    try {
      const result = await sendPrintEmailFn({ data: { merchantId: merchant.id } });
      if (!result?.ok) throw new Error(result?.error || "Send failed");
      track("print_email_sent", merchant.id);
      toast.success(t("poster.emailSent"));
    } catch (err) {
      console.error("Email failed:", err);
      toast.error(t("poster.emailFailed"));
    } finally {
      setEmailSending(false);
    }
  }, [merchant, t]);

  const handleDownloadPdf = useCallback(
    async (ref: React.RefObject<HTMLDivElement | null>, width: number, height: number, filename: string, isLandscape = false) => {
      if (!ref.current) return;
      try {
        const dataUrl = await toPng(ref.current, {
          cacheBust: true,
          pixelRatio: 3,
          backgroundColor: "#FAF8F5",
          width,
          height,
          skipFonts: true,
        });

        const pdf = new jsPDF({
          orientation: isLandscape ? "landscape" : "portrait",
          unit: "mm",
          format: isLandscape ? [55, 85] : "a4",
          compress: true,
        });

        const pdfW = isLandscape ? 85 : 210;
        const pdfH = isLandscape ? 55 : 297;
        pdf.addImage(dataUrl, "PNG", 0, 0, pdfW, pdfH, undefined, "FAST");
        pdf.save(filename);
      } catch (err) {
        console.error("PDF export failed:", err);
      }
    },
    []
  );

  const handleDownloadTenCards = useCallback(async () => {
    if (!tenCardRef.current) return;
    try {
      const dataUrl = await toPng(tenCardRef.current, {
        cacheBust: true,
        pixelRatio: 3,
        backgroundColor: "#FFFFFF",
        width: 595,
        height: 842,
        skipFonts: true,
      });

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      pdf.addImage(dataUrl, "PNG", 0, 0, 210, 297, undefined, "FAST");
      pdf.save(`${slug}-10cards.pdf`);
    } catch (err) {
      console.error("10-card PDF export failed:", err);
    }
  }, [slug]);

  // QR encodes both slug and the secret scan_token, so the URL is unguessable
  // even though it still looks like a normal slug to the customer.
  const scanPath = merchant ? `/tack/${slug}-${merchant.scan_token}` : `/tack/${slug}`;
  const feedbackUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${scanPath}`
      : scanPath;

  const primaryColor = merchant?.brand_primary_color || "hsl(20, 55%, 60%)";

  if (!loaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40">
        <p className="text-muted-foreground">{t("poster.loading")}</p>
      </div>
    );
  }

  if (!merchant) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-muted/40">
        <p className="text-xl text-muted-foreground">{t("poster.notFound")}</p>
        <p className="text-sm text-muted-foreground">
          {t("poster.notFoundDetail", { slug })}
        </p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @media print {
          .no-print, .edit-panel { display: none !important; }
        }
      `}</style>

      {/* Welcome banner after onboarding */}
      {showWelcome && (
        <div className="bg-primary/10 border-b border-primary/20 animate-fade-in">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-center gap-2">
            <Sparkles className="size-5 text-primary" />
            <p className="text-sm font-medium text-primary">
              {t("poster.welcomeBanner")}
            </p>
            <button
              onClick={() => setShowWelcome(false)}
              className="ml-2 text-primary/60 hover:text-primary text-xs"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Onboarding welcome dialog — shown once after setup */}
      <Dialog open={showOnboardingDialog} onOpenChange={setShowOnboardingDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">{t("poster.onboardingTitle")}</DialogTitle>
            <DialogDescription className="text-sm leading-relaxed pt-2 whitespace-pre-line">
              {t("poster.onboardingDesc")}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 pt-2">
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowPulse(false);
                  const doDownload = () => { track("poster_downloaded", merchant!.id, { format: "a4", source: "welcome_dialog" }); handleDownloadPdf(posterRef, 595, 842, `${slug}-poster.pdf`); };
                  setShowOnboardingDialog(false);
                  if (shouldShowPrintTips()) {
                    setPendingDownload(() => doDownload);
                    setTimeout(() => setShowPrintTips(true), 300);
                  } else {
                    doDownload();
                  }
                }}
                className="flex-1 bg-primary text-primary-foreground px-4 py-2.5 rounded-lg text-sm font-semibold shadow hover:bg-primary/90 transition-colors"
              >
                {t("poster.downloadA4")}
              </button>
              <button
                onClick={() => {
                  setShowPulse(false);
                  const doDownload = () => { track("card_downloaded", merchant!.id, { format: "ten_card_sheet", source: "welcome_dialog" }); handleDownloadTenCards(); };
                  setShowOnboardingDialog(false);
                  if (shouldShowPrintTips()) {
                    setPendingDownload(() => doDownload);
                    setTimeout(() => setShowPrintTips(true), 300);
                  } else {
                    doDownload();
                  }
                }}
                className="flex-1 bg-secondary text-secondary-foreground px-4 py-2.5 rounded-lg text-sm font-semibold shadow hover:bg-secondary/80 transition-colors"
              >
                {t("poster.download10Cards")}
              </button>
            </div>
            <button
              onClick={handleEmailPrintFiles}
              disabled={emailSending}
              className="flex items-center justify-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors py-1.5"
            >
              {emailSending ? <Loader2 className="size-4 animate-spin" /> : <Mail className="size-4" />}
              {emailSending ? t("poster.emailSending") : t("poster.emailToMe")}
            </button>

            <button
              onClick={() => {
                setShowOnboardingDialog(false);
                handleOrderStarterKit();
              }}
              disabled={checkoutLoading}
              className="w-full bg-primary hover:bg-primary/90 disabled:opacity-60 text-primary-foreground px-4 py-2.5 rounded-lg text-sm font-semibold shadow transition-colors flex items-center justify-center gap-2"
            >
              {checkoutLoading ? <Loader2 className="size-4 animate-spin" /> : <Package className="size-4" />}
              {t("poster.starterKitCta")}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hidden 10-card sheet for PDF export */}
      <div style={{ position: "absolute", left: -9999, top: 0 }}>
        <div
          ref={tenCardRef}
          style={{
            width: 595,
            height: 842,
            backgroundColor: "#FFFFFF",
            position: "relative",
            boxSizing: "border-box",
          }}
        >
          {Array.from({ length: 10 }).map((_, i) => {
            const col = i % 2;
            const row = Math.floor(i / 2);
            const cardW = 241;
            const cardH = 156;
            const marginX = 49;
            const marginY = 19;
            const gapX = 14;
            const gapY = 6;
            const x = marginX + col * (cardW + gapX);
            const y = marginY + row * (cardH + gapY);
            return (
              <div
                key={i}
                style={{
                  position: "absolute",
                  left: x,
                  top: y,
                  width: cardW,
                  height: cardH,
                  overflow: "hidden",
                }}
              >
                <div style={{ transform: "scale(0.4725)", transformOrigin: "top left", width: 510, height: 330 }}>
                  <BusinessCard
                    primaryColor={primaryColor}
                    feedbackUrl={feedbackUrl}
                    line1={cardLine1}
                    line2={cardLine2}
                    merchantName={merchant.business_name}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="print-wrapper min-h-screen overflow-auto bg-[#E8E4DF] flex justify-center p-3">
        <div className="w-full max-w-[1400px] flex flex-col md:flex-row gap-4">
        {/* Left: Edit Panel */}
        <div className="edit-panel w-full md:w-80 lg:w-96 flex-shrink-0 flex flex-col gap-3">
          {/* Language picker */}
          <div className="rounded-lg p-3" style={{ border: `1.5px solid ${primaryColor}`, backgroundColor: "#FFFFFF" }}>
            <Label className="text-[10px] text-muted-foreground block mb-1">{t("poster.language")}</Label>
            <select
              value={selectedLocale}
              onChange={(e) => handleLocaleChange(e.target.value)}
              className="flex h-8 w-full rounded-md border border-input bg-white px-2 text-xs"
            >
              {LOCALE_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.codes.map((code) => (
                    <option key={code} value={code}>{LOCALE_LABELS[code]}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            <p className="text-[10px] text-muted-foreground italic mt-1 leading-tight">
              {t("poster.languageHint")}
            </p>
          </div>

          {/* A4 Poster section */}
          <div className="rounded-lg p-3 space-y-2" style={{ border: `1.5px solid ${primaryColor}`, backgroundColor: "#FFFFFF" }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold" style={{ color: primaryColor }}>{t("poster.a4Poster")}</h2>
                <button
                  type="button"
                  onClick={() => setResetScope("poster")}
                  className="text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-2"
                >
                  {t("poster.reset")}
                </button>
              </div>
              <button
                onClick={() => {
                  setShowPulse(false);
                  const doDownload = () => { track("poster_downloaded", merchant.id, { format: "a4" }); handleDownloadPdf(posterRef, 595, 842, `${slug}-poster.pdf`); };
                  if (shouldShowPrintTips()) { setPendingDownload(() => doDownload); setShowPrintTips(true); } else { doDownload(); }
                }}
                className={`bg-primary text-primary-foreground px-2.5 py-1 rounded-md text-[11px] font-medium shadow hover:bg-primary/90 transition-colors ${showPulse ? "animate-pulse" : ""}`}
              >
                {t("poster.downloadPdf")}
              </button>
            </div>

            <div className="space-y-0.5">
              <Label htmlFor="topText" className="text-xs">{t("poster.headerSubtitle")}</Label>
              <Input
                id="topText"
                className="h-7 text-xs bg-white"
                value={topText}
                onChange={(e) => {
                  if (e.target.value.length <= 20) {
                    setTopText(e.target.value);
                    saveField("poster_top_text", e.target.value);
                  }
                }}
                placeholder={t("poster.topPlaceholder")}
                maxLength={20}
              />
              <p className="text-[10px] text-muted-foreground text-right leading-none">{topText.length}/20</p>
            </div>

            <div className="space-y-0.5">
              <Label htmlFor="bottomText" className="text-xs">{t("poster.callToAction")}</Label>
              <Input
                id="bottomText"
                className="h-7 text-xs bg-white"
                value={bottomText}
                onChange={(e) => {
                  if (e.target.value.length <= 40) {
                    setBottomText(e.target.value);
                    saveField("poster_bottom_text", e.target.value);
                  }
                }}
                placeholder={t("poster.bottomPlaceholder")}
                maxLength={40}
              />
              <p className="text-[10px] text-muted-foreground text-right leading-none">{bottomText.length}/40</p>
            </div>
          </div>

          {/* Business Card section */}
          <div className="rounded-lg p-3 space-y-2" style={{ border: `1.5px solid ${primaryColor}`, backgroundColor: "#FFFFFF" }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold" style={{ color: primaryColor }}>{t("poster.businessCard")}</h2>
                <button
                  type="button"
                  onClick={() => setResetScope("card")}
                  className="text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-2"
                >
                  {t("poster.reset")}
                </button>
              </div>
              <div className="flex gap-1.5">
                <button
                  onClick={() => {
                    setShowPulse(false);
                    const doDownload = () => { track("card_downloaded", merchant.id, { format: "single_card" }); handleDownloadPdf(cardRef, 510, 330, `${slug}-card.pdf`, true); };
                    if (shouldShowPrintTips()) { setPendingDownload(() => doDownload); setShowPrintTips(true); } else { doDownload(); }
                  }}
                  className={`bg-primary text-primary-foreground px-2 py-1 rounded-md text-[11px] font-medium shadow hover:bg-primary/90 transition-colors ${showPulse ? "animate-pulse" : ""}`}
                >
                  {t("poster.oneCard")}
                </button>
                <button
                  onClick={() => {
                    setShowPulse(false);
                    const doDownload = () => { track("card_downloaded", merchant.id, { format: "ten_card_sheet" }); handleDownloadTenCards(); };
                    if (shouldShowPrintTips()) { setPendingDownload(() => doDownload); setShowPrintTips(true); } else { doDownload(); }
                  }}
                  className={`bg-primary text-primary-foreground px-2 py-1 rounded-md text-[11px] font-medium shadow hover:bg-primary/90 transition-colors ${showPulse ? "animate-pulse" : ""}`}
                >
                  {t("poster.tenCardSheet")}
                </button>
              </div>
            </div>

            <div className="space-y-0.5">
              <Label htmlFor="cardLine2" className="text-xs">{t("poster.headerSubtitle")}</Label>
              <Input
                id="cardLine2"
                className="h-7 text-xs bg-white"
                value={cardLine2}
                onChange={(e) => {
                  if (e.target.value.length <= 20) {
                    setCardLine2(e.target.value);
                    saveField("card_line2", e.target.value);
                  }
                }}
                placeholder={t("poster.cardTopPlaceholder")}
                maxLength={20}
              />
              <p className="text-[10px] text-muted-foreground text-right leading-none">{cardLine2.length}/20</p>
            </div>

            <div className="space-y-0.5">
              <Label htmlFor="cardLine1" className="text-xs">{t("poster.callToAction")}</Label>
              <Input
                id="cardLine1"
                className="h-7 text-xs bg-white"
                value={cardLine1}
                onChange={(e) => {
                  if (e.target.value.length <= 40) {
                    setCardLine1(e.target.value);
                    saveField("card_line1", e.target.value);
                  }
                }}
                placeholder={t("poster.cardBottomPlaceholder")}
                maxLength={40}
              />
              <p className="text-[10px] text-muted-foreground text-right leading-none">{cardLine1.length}/40</p>
            </div>
          </div>

          {/* Starter kit inline CTA */}
          <div className="rounded-lg p-3 space-y-2" style={{ border: `1.5px solid ${primaryColor}`, backgroundColor: "#FFF7EF" }}>
            <div className="flex items-start gap-2">
              <Package className="size-4 mt-0.5 flex-shrink-0" style={{ color: primaryColor }} />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold" style={{ color: primaryColor }}>
                  {t("poster.starterKitInlineTitle")}
                </h3>
                <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                  {t("poster.starterKitInlineDesc")}
                </p>
              </div>
            </div>
            <button
              onClick={handleOrderStarterKit}
              disabled={checkoutLoading}
              className="w-full bg-primary hover:bg-primary/90 disabled:opacity-60 text-primary-foreground px-3 py-2 rounded-md text-xs font-semibold shadow transition-colors flex items-center justify-center gap-1.5"
            >
              {checkoutLoading ? <Loader2 className="size-3.5 animate-spin" /> : null}
              {t("poster.starterKitInlineCta")}
            </button>
          </div>

          <p className="text-[10px] italic leading-relaxed mt-1" style={{ color: "#2D2D2D" }}>
            {t("poster.customizeHint")}
          </p>

        </div>

        {/* Right: Live Previews side by side */}
        <div className="flex-1 flex flex-col md:flex-row gap-4 items-start md:justify-center overflow-auto">
          {/* A4 Preview */}
          <div className="flex-shrink-0" style={{ width: 595 * 0.52, height: 842 * 0.52 }}>
            <div style={{ transform: "scale(0.52)", transformOrigin: "top left" }}>
              <PosterCard
                posterRef={posterRef}
                primaryColor={primaryColor}
                feedbackUrl={feedbackUrl}
                topText={topText}
                bottomText={bottomText}
                merchantName={merchant.business_name}
              />
            </div>
          </div>

          {/* Business Card Preview - right of A4 */}
          <div className="flex-shrink-0" style={{ width: 510 * 0.5, height: 330 * 0.5 }}>
            <div style={{ transform: "scale(0.5)", transformOrigin: "top left" }}>
              <BusinessCard
                cardRef={cardRef}
                primaryColor={primaryColor}
                feedbackUrl={feedbackUrl}
                line1={cardLine1}
                line2={cardLine2}
                merchantName={merchant.business_name}
              />
            </div>
          </div>
        </div>

        </div>
      </div>

      <PrintTipsDialog open={showPrintTips} onOpenChange={setShowPrintTips} onProceed={() => { pendingDownload?.(); setPendingDownload(null); }} />

      <AlertDialog open={resetScope !== null} onOpenChange={(open) => !open && setResetScope(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("poster.resetConfirmTitle", { language: LOCALE_LABELS[selectedLocale] || selectedLocale })}</AlertDialogTitle>
            <AlertDialogDescription>
              {resetScope === "card"
                ? t("poster.resetCardDesc", { language: LOCALE_LABELS[selectedLocale] || selectedLocale })
                : t("poster.resetPosterDesc", { language: LOCALE_LABELS[selectedLocale] || selectedLocale })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (resetScope) handleReset(resetScope);
                setResetScope(null);
              }}
            >
              {t("poster.reset")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {merchant && user ? (
        <StarterKitAddressDialog
          open={addressDialogOpen}
          onOpenChange={setAddressDialogOpen}
          merchantId={merchant.id}
          userId={user.id}
          userEmail={user.email}
          slug={slug}
          defaultCompanyName={merchant.business_name}
        />
      ) : null}
      {checkoutDialog}
    </>
  );
}
